import { environment } from "@app/relay/relay-environment";
import { useState } from "react";
import { commitMutation, graphql, useLazyLoadQuery } from "react-relay";

import type { ownerDistributionsRow_Query } from "@app/queries/ownerDistributionsRow_Query.graphql";
import type { ownerDistributionsRow_ToggleMutation } from "@app/queries/ownerDistributionsRow_ToggleMutation.graphql";
import { CellLink } from "@app/components/ui/cell-link";

const distributionsQuery = graphql`
  query ownerDistributionsRow_Query($year: Int) {
    ownerDistributions(year: $year) {
      stakeholder {
        id
        name
      }
      flagged
      yearTotal
      points {
        month
        value
      }
    }
    allStakeholders {
      id
      name
      cxid
      distribution
    }
  }
`;

const toggleMutation = graphql`
  mutation ownerDistributionsRow_ToggleMutation(
    $input: StakeholderDistributionSetInputObject!
  ) {
    stakeholderDistributionSet(input: $input) {
      stakeholder {
        id
        distribution
      }
      errors {
        message
      }
    }
  }
`;

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const monthLabel = (yyyymm: string) => MONTH_LABELS[Number(yyyymm.slice(5, 7)) - 1] ?? yyyymm;

const money = (value: number) =>
  value.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

// The "Owner distributions" panel (cost-recognition basis layer, U0b): shared
// across every perspective, kept OUT of the cost tree (owner draws are excluded
// from operating cost/revenue). Shows one row per stakeholder with 1+ distribution
// outflows in the selected year — amounts driven by the authoritative
// txns.op_distribution bit (the union of the structural distribution flag and the
// per-txn DIVIDEND classification). Flagged stakeholders are always listed (even
// at zero this year) so the flag stays manageable; classification-only rows carry
// amounts but no remove control (there is no flag to clear).
//
// Editing the flag writes stakeholders.distribution through stakeholderDistributionSet
// (a Layer-4 property edit, NOT a per-perspective categorization). The mutation
// re-stamps the per-txn verdict server-side, so a fetchKey bump refetches the
// amounts without a rebuild.
export default function OwnerDistributionsRow({
  year,
  onOpenStakeholderMonth,
}: {
  year: number;
  onOpenStakeholderMonth: (stakeholder: { id: string; name: string }, month: string) => void;
}) {
  const [fetchKey, setFetchKey] = useState(0);
  const data = useLazyLoadQuery<ownerDistributionsRow_Query>(
    distributionsQuery,
    { year },
    { fetchPolicy: "store-and-network", fetchKey },
  );
  const [adding, setAdding] = useState(false);

  // Exclude structural nodes (ROOT "#…" / BASE "&…") — they are never owner draws.
  const selectable = data.allStakeholders.filter(
    (stakeholder) => !stakeholder.cxid.startsWith("#") && !stakeholder.cxid.startsWith("&"),
  );
  const unflagged = selectable.filter((stakeholder) => !stakeholder.distribution);

  // Rows: the year's distribution amounts first (already ordered by year total
  // descending, structural nodes dropped server-side), then any flagged
  // stakeholder with no outflows this year so it stays removable.
  const present = new Set(data.ownerDistributions.map((row) => row.stakeholder.id));
  const flaggedExtras = selectable.filter(
    (stakeholder) => stakeholder.distribution && !present.has(stakeholder.id),
  );

  const toggle = (stakeholderId: string, distribution: boolean) => {
    commitMutation<ownerDistributionsRow_ToggleMutation>(environment, {
      mutation: toggleMutation,
      variables: { input: { stakeholderId, distribution } },
      onCompleted: (_response, errors) => {
        if (errors?.length) {
          window.alert(errors[0].message);
          return;
        }
        // Re-stamped verdict → refetch so amounts and row membership update.
        setFetchKey((key) => key + 1);
      },
      onError: (error) => window.alert(error.message),
    });
    setAdding(false);
  };

  const isEmpty = data.ownerDistributions.length === 0 && flaggedExtras.length === 0;

  return (
    <div className="mb-4 overflow-x-auto rounded-lg border border-gray-200 bg-white p-3">
      <div className="mb-2 flex items-center gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Owner distributions
        </span>
        <span className="text-xs text-gray-400">
          shared across all perspectives — excluded from operating cost &amp; revenue
        </span>
        {adding ? (
          <select
            autoFocus
            defaultValue=""
            className="ml-auto rounded border border-gray-300 px-2 py-1 text-sm"
            onChange={(event) => event.target.value && toggle(event.target.value, true)}
            onBlur={() => setAdding(false)}
          >
            <option value="" disabled>
              Add a stakeholder…
            </option>
            {unflagged.map((stakeholder) => (
              <option key={stakeholder.id} value={stakeholder.id}>
                {stakeholder.name}
              </option>
            ))}
          </select>
        ) : (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="ml-auto rounded-full border border-dashed border-gray-300 px-2.5 py-1 text-sm text-gray-500 hover:border-gray-400 hover:text-gray-700"
          >
            + Add
          </button>
        )}
      </div>

      {isEmpty ? (
        <span className="text-sm text-gray-400">No owner-distribution stakeholders yet.</span>
      ) : (
        <table className="min-w-[1280px] w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-gray-200 text-xs uppercase text-gray-400">
              <th className="sticky left-0 z-10 min-w-[20rem] bg-white px-3 py-2 text-left font-medium">
                Stakeholder
              </th>
              {MONTH_LABELS.map((label) => (
                <th key={label} className="whitespace-nowrap px-3 py-2 text-right font-medium tabular-nums">
                  {label}
                </th>
              ))}
              <th className="whitespace-nowrap px-3 py-2 text-right font-medium tabular-nums">Year</th>
            </tr>
          </thead>
          <tbody>
            {data.ownerDistributions.map((row) => (
              <tr key={row.stakeholder.id} className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50/60">
                <td className="sticky left-0 z-10 min-w-[20rem] bg-white px-3 py-1.5">
                  <div className="flex items-center gap-1.5">
                    <span className="truncate text-gray-700">{row.stakeholder.name}</span>
                    {row.flagged && (
                      <button
                        type="button"
                        onClick={() => toggle(row.stakeholder.id, false)}
                        title="Remove from owner distributions"
                        className="ml-1 text-amber-500 hover:text-amber-900"
                      >
                        ×
                      </button>
                    )}
                  </div>
                </td>
                {row.points.map((point) => {
                  const value = point.value ?? 0;
                  return (
                    <td key={point.month} className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums">
                      {value <= 0 ? (
                        <span className="text-gray-300">—</span>
                      ) : (
                        <CellLink
                          onClick={() => onOpenStakeholderMonth(row.stakeholder, point.month)}
                          title={`View ${monthLabel(point.month)} distributions`}
                        >
                          {money(value)}
                        </CellLink>
                      )}
                    </td>
                  );
                })}
                <td className="whitespace-nowrap px-3 py-1.5 text-right font-medium tabular-nums text-gray-700">
                  {money(row.yearTotal)}
                </td>
              </tr>
            ))}
            {flaggedExtras.map((stakeholder) => (
              <tr key={stakeholder.id} className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50/60">
                <td className="sticky left-0 z-10 min-w-[20rem] bg-white px-3 py-1.5">
                  <div className="flex items-center gap-1.5">
                    <span className="truncate text-gray-700">{stakeholder.name}</span>
                    <button
                      type="button"
                      onClick={() => toggle(stakeholder.id, false)}
                      title="Remove from owner distributions"
                      className="ml-1 text-amber-500 hover:text-amber-900"
                    >
                      ×
                    </button>
                  </div>
                </td>
                {MONTH_LABELS.map((label) => (
                  <td key={label} className="px-3 py-1.5 text-right tabular-nums">
                    <span className="text-gray-300">—</span>
                  </td>
                ))}
                <td className="px-3 py-1.5 text-right tabular-nums text-gray-300">—</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
